import styled from "styled-components";
import { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home } from 'lucide-react';
import { ReactSVG } from 'react-svg';
import { purpleColors, violetColors, slateColors, Section, Container, fontFamily } from "./AppLandingPage/styles";
import { ButtonM } from '../Controls/Buttons';

const PageSection = styled(Section)`
  background: linear-gradient(to bottom right, ${slateColors[50]}, white);
  padding-top: 4rem;
  padding-bottom: 5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  min-height: 60vh;

  @media (min-width: 640px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
`;

const PageContainer = styled(Container)`
  max-width: 70rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;
`;

const HeroCard = styled.div`
  background: linear-gradient(to right, ${purpleColors[50]}, ${violetColors[500]}12);
  border: 1px solid ${purpleColors[100]};
  border-radius: 1.5rem;
  padding: 3rem 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 42rem;
  width: 100%;
  text-align: center;
  box-sizing: border-box;

  @media (min-width: 640px) {
    padding: 3rem 2rem;
  }

  @media (min-width: 768px) {
    padding: 4rem 3rem;
    gap: 2rem;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ErrorCode = styled.div`
  font-size: 5rem;
  font-weight: 800;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 6.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 8rem;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 60px;
    height: auto;

    @media (min-width: 768px) {
      width: 75px;
    }

    @media (min-width: 1024px) {
      width: 90px;
    }
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: ${slateColors[900]};
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Description = styled.p`
  margin: 0;
  color: ${slateColors[600]};
  font-size: 1.125rem;
  line-height: 1.6;
  max-width: 32rem;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const NotFoundPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>404 - Page Not Found | chuuchuu</title>
        <meta name="description" content="The page you are looking for could not be found." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PageSection>
        <PageContainer>
          <HeroCard>
            <ErrorContainer>
              <IconContainer>
                <ReactSVG
                  src="/images/sad chuuchuu icon.svg"
                  beforeInjection={(svg) => {
                    // Set fill color on all path elements to purple
                    const paths = svg.querySelectorAll('path');
                    paths.forEach((path) => {
                      path.setAttribute('fill', purpleColors[600]);
                    });
                  }}
                />
              </IconContainer>
              <ErrorCode>404</ErrorCode>
            </ErrorContainer>
            <PageTitle>{t('notFound.title') || 'Page Not Found'}</PageTitle>
            <Description>
              {t('notFound.description') || "Sorry, we couldn't find the page you're looking for. The page may have been moved, deleted, or the URL might be incorrect."}
            </Description>
            <ButtonContainer>
              <ButtonM onClick={() => navigate('/')}>
                <Home size={20} />
                {t('notFound.goHome') || 'Go to Home'}
              </ButtonM>
            </ButtonContainer>
          </HeroCard>
        </PageContainer>
      </PageSection>
    </>
  );
};

export default NotFoundPage;

